/**
 * Managing the public trust page.
 *
 * Everything a stranger will see lives on one screen — the address, the
 * introduction, the documents, and the terms they have to accept — because
 * these are one decision, not four settings.
 *
 * The access log is the part worth building. Hosting a PDF is not interesting;
 * being able to answer "who at the customer read our pentest summary, and did
 * they actually open it" is.
 */

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  api,
  errorMessage,
  type AccessRecord,
  type Account,
  type LimitCheck,
  type TrustDocument,
} from '../api';
import { link } from '../router';
import {
  Alert,
  Button,
  Dialog,
  EmptyState,
  Field,
  Input,
  PageHeader,
  Spinner,
  Textarea,
  formatDate,
} from '../ui';
import {
  CheckIcon,
  CopyIcon,
  ExternalLinkIcon,
  EyeIcon,
  EyeOffIcon,
  FileTextIcon,
  Icon,
  IconHover,
  SparklesIcon,
  Trash2Icon,
  UploadIcon,
} from '../icons';

const DEFAULT_TERMS = `The material behind this page is confidential information of {company}.

By continuing you confirm that you are requesting it on behalf of your employer for the purpose of evaluating {company} as a vendor, that you will not share it outside your organisation, and that you will destroy or delete it when that evaluation ends.`;

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function UploadDialog({
  onClose,
  onUploaded,
}: {
  onClose: () => void;
  onUploaded: () => void;
}) {
  const [file, setFile] = useState<File | null>(null);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  const submit = async () => {
    if (!file) return;
    setBusy(true);
    setError(null);
    try {
      await api.uploadDocument({ file, title: title.trim() || file.name, description });
      onUploaded();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
    }
  };

  return (
    <Dialog
      title="Add a document"
      description="Listed publicly; downloadable only after someone identifies themselves and accepts your terms."
      onClose={onClose}
      footer={
        <>
          <Button onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon={CheckIcon} busy={busy} disabled={!file} onClick={submit}>
            Add
          </Button>
        </>
      }
    >
      {error ? (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      <div className="space-y-4">
        <IconHover
          className="flex cursor-pointer flex-col items-center rounded-xl border border-dashed border-line-strong px-6 py-8 text-center"
          onClick={() => input.current?.click()}
          onDragOver={(e: React.DragEvent) => e.preventDefault()}
          onDrop={(e: React.DragEvent) => {
            e.preventDefault();
            const dropped = e.dataTransfer.files[0];
            if (dropped) setFile(dropped);
          }}
        >
          <span className="mb-2 flex h-9 w-9 items-center justify-center rounded-full bg-sunken text-muted">
            <Icon icon={UploadIcon} size={17} />
          </span>
          {file ? (
            <>
              <p className="text-[13.5px] font-medium">{file.name}</p>
              <p className="mt-0.5 text-[12.5px] text-faint">{formatSize(file.size)}</p>
            </>
          ) : (
            <>
              <p className="text-[13.5px] font-medium">Choose a file</p>
              <p className="mt-0.5 text-[12.5px] text-faint">
                PDF, Word, Excel, PNG, JPEG or text — up to 25 MB
              </p>
            </>
          )}
          <input
            ref={input}
            type="file"
            accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] ?? null)}
          />
        </IconHover>

        <Field label="Title" hint="What a buyer will see in the list.">
          <Input
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            placeholder="SOC 2 Type II report (2026)"
          />
        </Field>

        <Field label="Description">
          <Textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={3}
            placeholder="Covers security and availability, audited by …, period ending March 2026."
          />
        </Field>
      </div>
    </Dialog>
  );
}

export function TrustPageView({
  account,
  onUpdated,
}: {
  account: Account;
  onUpdated: (account: Account) => void;
}) {
  const [slug, setSlug] = useState(account.trustSlug);
  const [intro, setIntro] = useState(account.trustIntro);
  const [terms, setTerms] = useState(account.trustTerms);
  const [published, setPublished] = useState(account.trustPublished);
  const [documents, setDocuments] = useState<TrustDocument[] | null>(null);
  const [accessLog, setAccessLog] = useState<AccessRecord[]>([]);
  const [limit, setLimit] = useState<LimitCheck | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);

  const url = `${window.location.origin}/trust/${slug}`;

  const load = useCallback(async () => {
    try {
      const data = await api.listDocuments();
      setDocuments(data.documents);
      setAccessLog(data.accessLog);
      setLimit(data.limit);
    } catch (err) {
      setError(errorMessage(err));
      setDocuments([]);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const save = async () => {
    setBusy(true);
    setError(null);
    setSaved(false);
    try {
      const { account: updated } = await api.updateSettings({
        trustSlug: slug,
        trustIntro: intro,
        trustTerms: terms,
        trustPublished: published,
      });
      onUpdated(updated);
      setSlug(updated.trustSlug);
      setSaved(true);
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (doc: TrustDocument) => {
    if (!confirm(`Delete "${doc.title}"? Existing download links stop working.`)) return;
    await api.deleteDocument(doc.id).catch(() => undefined);
    await load();
  };

  const togglePublished = async (doc: TrustDocument) => {
    await api.updateDocument(doc.id, { published: !doc.published }).catch(() => undefined);
    await load();
  };

  const outOfRoom = limit !== null && !limit.allowed;

  return (
    <>
      <PageHeader
        title="Trust page"
        description="What a buyer sees before they send you anything. Published cards and documents are public; stale evidence is withheld automatically."
        actions={
          published ? (
            <IconHover
              as="a"
              className="btn btn-secondary"
              href={`/trust/${slug}`}
              target="_blank"
              rel="noreferrer"
            >
              <Icon icon={ExternalLinkIcon} size={16} />
              View
            </IconHover>
          ) : null
        }
      />

      {error ? (
        <div className="mb-4">
          <Alert>{error}</Alert>
        </div>
      ) : null}

      <div className="space-y-4">
        <section className="card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <h2 className="text-[15px] font-medium">Address and introduction</h2>
            <label className="flex cursor-pointer items-center gap-2 text-[13px]">
              <input
                type="checkbox"
                className="accent-[var(--vk-accent)]"
                checked={published}
                onChange={(e) => setPublished(e.target.checked)}
              />
              Published
            </label>
          </div>

          <div className="mt-4 space-y-4">
            <Field label="Address" hint="Letters, numbers and hyphens.">
              <Input value={slug} onChange={(e) => setSlug(e.target.value)} />
            </Field>

            <div className="flex flex-wrap items-center gap-2 rounded-lg bg-sunken px-3 py-2 text-[13px]">
              <span className="citation truncate text-muted">{url}</span>
              <Button
                small
                variant="ghost"
                icon={CopyIcon}
                className="ml-auto"
                onClick={() => void navigator.clipboard?.writeText(url)}
              >
                Copy
              </Button>
            </div>

            <Field label="Introduction" hint="Shown above your evidence.">
              <Textarea
                value={intro}
                onChange={(e) => setIntro(e.target.value)}
                rows={3}
                placeholder="Acme is a B2B analytics platform. Everything below is current as of its review date."
              />
            </Field>
          </div>
        </section>

        <section className="card p-5">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <h2 className="text-[15px] font-medium">Documents</h2>
              <p className="mt-1 max-w-xl text-[13px] text-muted">
                Listed publicly, downloadable only after a visitor gives their name, work email and
                company, and accepts your terms below.
              </p>
            </div>
            <Button
              variant="primary"
              icon={UploadIcon}
              disabled={outOfRoom}
              onClick={() => setUploading(true)}
            >
              Add document
            </Button>
          </div>

          {outOfRoom ? (
            <div className="mt-4">
              <Alert tone="warn">
                {limit?.limit === 0
                  ? 'Trust page documents are not part of your plan.'
                  : `You have used all ${limit?.limit} documents on your plan.`}{' '}
                <a {...link('/pricing')} className="underline">
                  See plans
                </a>
                .
              </Alert>
            </div>
          ) : null}

          <div className="mt-4">
            {documents === null ? (
              <div className="flex justify-center py-8">
                <Spinner />
              </div>
            ) : documents.length === 0 ? (
              <EmptyState
                icon={FileTextIcon}
                title="No documents yet"
                action={
                  outOfRoom ? (
                    <a {...link('/pricing')}>
                      <Button icon={SparklesIcon}>See plans</Button>
                    </a>
                  ) : (
                    <Button variant="primary" icon={UploadIcon} onClick={() => setUploading(true)}>
                      Add the first one
                    </Button>
                  )
                }
              >
                A SOC 2 report, a pentest summary, an architecture overview — the things a buyer
                asks for by email today.
              </EmptyState>
            ) : (
              <div className="space-y-2.5">
                {documents.map((doc) => {
                  const reads = accessLog.filter((a) => a.documentId === doc.id);
                  return (
                    <IconHover key={doc.id} className="rounded-lg border border-line p-4">
                      <div className="flex flex-wrap items-start justify-between gap-3">
                        <div className="min-w-0">
                          <h3 className="text-[14px] font-medium">{doc.title}</h3>
                          <p className="mt-0.5 truncate text-[12.5px] text-faint">
                            {doc.filename} · {formatSize(doc.sizeBytes)}
                          </p>
                          {doc.description ? (
                            <p className="mt-1.5 text-[13px] text-muted">{doc.description}</p>
                          ) : null}
                        </div>
                        <span className={`chip ${reads.length > 0 ? 'chip-accent' : 'chip-neutral'}`}>
                          {reads.length} {reads.length === 1 ? 'request' : 'requests'}
                        </span>
                      </div>

                      <div className="mt-3 flex flex-wrap items-center gap-1 border-t border-line pt-3">
                        <Button
                          small
                          variant="ghost"
                          icon={doc.published ? EyeIcon : EyeOffIcon}
                          onClick={() => togglePublished(doc)}
                        >
                          {doc.published ? 'Listed' : 'Hidden'}
                        </Button>
                        <Button
                          small
                          variant="ghost"
                          icon={Trash2Icon}
                          className="ml-auto"
                          onClick={() => remove(doc)}
                        >
                          Delete
                        </Button>
                      </div>
                    </IconHover>
                  );
                })}
              </div>
            )}
          </div>
        </section>

        <section className="card p-5">
          <h2 className="text-[15px] font-medium">Terms</h2>
          <p className="mt-1 max-w-xl text-[13px] text-muted">
            Shown before a document downloads, and stored word for word with each acceptance — so
            editing this later never rewrites what somebody already agreed to. Leave it empty and
            the gate only asks who they are.
          </p>
          <div className="mt-4">
            <Textarea
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              rows={7}
              placeholder={DEFAULT_TERMS.replace(/\{company\}/g, account.companyName)}
            />
            {terms.trim() === '' ? (
              <Button
                small
                className="mt-2"
                onClick={() => setTerms(DEFAULT_TERMS.replace(/\{company\}/g, account.companyName))}
              >
                Use a starting point
              </Button>
            ) : null}
          </div>
        </section>

        <section className="card p-5">
          <h2 className="text-[15px] font-medium">Who has asked</h2>
          <p className="mt-1 text-[13px] text-muted">
            Every acceptance, and whether the file was actually opened.
          </p>

          {accessLog.length === 0 ? (
            <p className="mt-4 text-[13px] text-faint">Nobody has requested a document yet.</p>
          ) : (
            <div className="mt-4 overflow-x-auto">
              <table className="w-full min-w-[36rem] border-collapse text-left text-[13px]">
                <thead>
                  <tr className="border-b border-line text-[12px] uppercase tracking-wide text-faint">
                    <th className="py-2 pr-4 font-medium">Who</th>
                    <th className="py-2 pr-4 font-medium">Company</th>
                    <th className="py-2 pr-4 font-medium">Document</th>
                    <th className="py-2 font-medium">Accepted</th>
                  </tr>
                </thead>
                <tbody className="text-muted">
                  {accessLog.map((record, i) => (
                    <tr key={`${record.email}-${record.acceptedAt}-${i}`} className="border-b border-line last:border-b-0">
                      <td className="py-2.5 pr-4">
                        <span className="block text-ink">{record.name}</span>
                        <span className="text-faint">{record.email}</span>
                      </td>
                      <td className="py-2.5 pr-4">{record.company}</td>
                      <td className="py-2.5 pr-4">
                        {documents?.find((d) => d.id === record.documentId)?.title ?? '—'}
                      </td>
                      <td className="py-2.5">
                        {formatDate(record.acceptedAt)}
                        {record.downloadedAt ? (
                          <span className="chip chip-accent ml-2">Opened</span>
                        ) : (
                          <span className="chip chip-neutral ml-2">Not opened</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>

        <div className="flex items-center gap-3">
          <Button variant="primary" icon={CheckIcon} busy={busy} onClick={save}>
            Save trust page
          </Button>
          {saved ? <span className="text-[13px] text-accent">Saved.</span> : null}
        </div>
      </div>

      {uploading ? (
        <UploadDialog
          onClose={() => setUploading(false)}
          onUploaded={() => {
            setUploading(false);
            void load();
          }}
        />
      ) : null}
    </>
  );
}
